import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowOutwardOutlined,
  ClearOutlined,
  ExpandMoreOutlined,
  LanOutlined,
  ListAltOutlined,
  Search,
  TimelineOutlined,
} from "@mui/icons-material";
import { useMemo, useRef, useState, useEffect } from "react";
import { useTheme } from "@emotion/react";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";

import LineChartComponent from "../../../../../components/lineComponent";
import CardCountPrefixes from "../../../../../components/dashBgp/minimalistCard/cardCountPrefixes";
import PrefixesModal from "../../../../../components/modalPrefixesReceived";
import useStyles from "./styles";

const timeZone = "America/Sao_Paulo";
const ITEMS_PER_PAGE = 8;

function extractIpFromInterface(interfaceName) {
  if (!interfaceName || typeof interfaceName !== "string") return "";
  let target = interfaceName.trim();
  if (target.includes("<->")) {
    const parts = target.split("<->");
    target = parts[parts.length - 1].trim();
  } else if (target.includes(" - ")) {
    const parts = target.split(" - ");
    target = parts[parts.length - 1].trim();
  }
  return target.replace(/^\[.*?\]\s*/, "").trim();
}

export default function SessoesBgp({
  hostsCliente,
  sessions,
  filterData,
  loading,
  fetchPrefixesForInterface,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const isDark = theme.palette.mode === "dark";

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [accordionOpen, setAccordionOpen] = useState(null);
  const [modalState, setModalState] = useState({
    open: false,
    interfaceName: "",
    ipSession: "",
    hostName: "",
    prefixes: [],
    loading: false,
  });

  /** Abre modal e busca prefixos sob demanda, apenas para a sessão clicada */
  async function handleOpenPrefixesModal({ interfaceName, ip, host }) {
    setModalState({
      open: true,
      interfaceName,
      ipSession: ip,
      hostName: host?.host,
      prefixes: [],
      loading: true,
    });

    const prefixes = await fetchPrefixesForInterface?.({
      host: host?.host,
      interfaceName,
    });

    setModalState((prev) =>
      prev?.interfaceName === interfaceName
        ? { ...prev, prefixes: prefixes || [], loading: false }
        : prev,
    );
  }

  /** Helpers */
  function getBgpItemByInterface(bgpStatusObj, interfaceName) {
    if (!bgpStatusObj || !interfaceName) return null;

    const cleaned = interfaceName.replace(/^\[.*?\]\s*/, "").trim();
    //const [device, ip] = cleaned.split(" - ").map((s) => s.trim());
    const [device, ip] = interfaceName.split(" - ").map((s) => s?.trim());

    return (
      Object.entries(bgpStatusObj || {}).find(([key]) => {
        return (
          key.includes(`[${device},${ip}F]`) ||
          key.includes(device) ||
          key.includes(ip)
        );
      })?.[1] || null
    );
  }

  /** Pagination */
  const bgpItems = useMemo(() => {
    const host = hostsCliente?.sessions?.[filterData?.serviceId]?.find(
      (s) => s?.hostId === filterData?.hostId,
    );
    return (
      host?.hostsClienteInterfaces?.map((interfaceName) => ({
        host,
        interfaceName,
      })) || []
    );
  }, [hostsCliente, filterData]);

  const filteredBgpItems = useMemo(() => {
    if (!search) return bgpItems;

    const searchLower = search.toLowerCase();

    return (
      bgpItems?.filter((item) =>
        Object.values(item || {}).some((value) =>
          String(value).toLowerCase().includes(searchLower),
        ),
      ) || []
    );
  }, [bgpItems, search]);

  const paginatedItems = useMemo(() => {
    const start = page * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredBgpItems?.slice(start, end) || [];
  }, [filteredBgpItems, page]);

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paperHeader}>
        {!!loading && <LinearProgress sx={styles.linearProgress} />}
        {/** Title */}
        <Box sx={styles.boxTitle}>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            <LanOutlined color="primary" />
            <Typography>Sessões</Typography>
          </Box>

          <TextField
            size="small"
            variant="outlined"
            label="Buscar"
            placeholder="Buscar sessão"
            value={search || ""}
            disabled={loading}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0); // reseta paginação
              setAccordionOpen(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearch("");
                      setPage(0);
                      setAccordionOpen(null);
                    }}
                    aria-label="Limpar busca"
                  >
                    <ClearOutlined fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
        <Divider />
        {/** Header */}
        <Grid container spacing={2} sx={{ pr: 5, pl: 2, py: 1 }}>
          <Grid item xs={5}>
            <Typography variant="body2">Sessão</Typography>
          </Grid>
          <Grid item xs={1.5}>
            <Typography variant="body2">Tempo Ativo / Inativo</Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography variant="body2">Anunciados</Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography variant="body2">Recebidos</Typography>
          </Grid>
          <Grid item xs={1.5}>
            <Typography variant="body2">Lista De Recebidos</Typography>{" "}
          </Grid>
          <Grid item xs={2}>
            <Typography variant="body2">Tendência</Typography>
          </Grid>
        </Grid>
        <Divider />
      </Paper>
      {/** Accordion */}
      <Box sx={styles.accordionContainer}>
        {!!hostsCliente?.sessions &&
          paginatedItems?.map(({ host, interfaceName }, index) => (
            <Accordion
              key={interfaceName}
              expanded={accordionOpen === interfaceName}
              sx={styles.accordion}
            >
              {/** Accordion Summary */}
              <AccordionSummary
                expandIcon={<ExpandMoreOutlined />}
                onClick={() => {
                  setAccordionOpen((prev) =>
                    prev === interfaceName ? null : interfaceName,
                  );
                }}
                sx={{
                  ...styles.accordionSummary,
                  backgroundColor:
                    index % 2 === 0
                      ? "transparent" //alpha(theme.palette.action.hover, 0.1) // linhas pares
                      : theme.palette.mode == "dark"
                        ? theme.palette.secondary.dark
                        : theme.palette.secondary.lighter, //"transparent", // linhas impares
                }}
              >
                <Grid container spacing={2}>
                  {/** PEER */}
                  <Grid item xs={5}>
                    <Box sx={styles.summaryInterfaceContainer}>
                      {/* 2: "Connect",  tentando estabelecer a conexão TCP.
                        3: "Active", tentando negociar a sessão BGP, mas ainda não conseguiu estabelecer a sessão
                        6: "Up", Sessão BGP estabelecida com sucesso */}
                      {(() => {
                        const bgpItem = getBgpItemByInterface(
                          sessions?.bgpStatusLastItem?.[host?.hostId],
                          interfaceName,
                        );
                        const hasData =
                          bgpItem &&
                          bgpItem.value !== undefined &&
                          bgpItem.value !== null &&
                          bgpItem.value !== "";
                        const isUp = hasData && Number(bgpItem.value) === 6;

                        const content = (
                          <Box
                            sx={{
                              ...styles.summaryInterfaceStatus,
                              ...(hasData
                                ? isUp
                                  ? {
                                      bgcolor: theme.palette.colors.green.light,
                                    }
                                  : {
                                      bgcolor: theme.palette.colors.red.light,
                                      animation: "pulse 1.5s infinite",
                                    }
                                : {
                                    bgcolor: theme.palette.colors.gray.light,
                                    animation: "pulse 1.5s infinite",
                                  }),
                            }}
                          />
                        );

                        if (!hasData) {
                          return (
                            <Tooltip
                              title="Não houveram coletas na última hora"
                              arrow
                              placement="top"
                            >
                              {content}
                            </Tooltip>
                          );
                        }
                        return content;
                      })()}

                      <Typography variant="body2">
                        {interfaceName?.replace("", "")?.trim()}
                      </Typography>
                    </Box>
                  </Grid>
                  {/** UPTIME */}
                  <Grid item xs={1.5}>
                    <Typography variant="body2">
                      {(() => {
                        const formated = formatDuration(
                          getBgpItemByInterface(
                            sessions?.bgpUptimeLastItem?.[host?.hostId],
                            interfaceName,
                          )?.value,
                        );

                        if (!formated) return "-";

                        return `${formated.d ?? 0}d ${formated.h ?? 0}h ${
                          formated.m ?? 0
                        }m`;
                      })()}
                    </Typography>
                  </Grid>
                  {/** ANNOUNCED */}
                  <Grid item xs={1}>
                    <Box sx={styles.summaryPrefixes}>
                      <ArrowOutwardOutlined
                        fontSize="16px"
                        sx={styles.summaryAnnouncedIcon}
                      />
                      <Typography variant="body2">
                        {parseInt(
                          getBgpItemByInterface(
                            sessions?.announcedPrefixesLastItem?.[host?.hostId],
                            interfaceName,
                          )?.value ?? 0,
                        ) ?? "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  {/** RECEIVED */}
                  <Grid item xs={1}>
                    <Box sx={styles.summaryPrefixes}>
                      <ArrowOutwardOutlined
                        fontSize="12px"
                        sx={styles.summaryReceivedIcon}
                      />
                      <Typography variant="body2">
                        {parseInt(
                          getBgpItemByInterface(
                            sessions?.receivedPrefixesLastItem?.[host?.hostId],
                            interfaceName,
                          )?.value ?? 0,
                        ) ?? "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  {/** Listar recebidos */}
                  <Grid item xs={1.5}>
                    {(() => {
                      const ip = extractIpFromInterface(interfaceName);
                      return (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<ListAltOutlined fontSize="small" />}
                          onClick={(e) => {
                            e?.stopPropagation?.();
                            handleOpenPrefixesModal({
                              interfaceName,
                              ip,
                              host,
                            });
                          }}
                          sx={styles.detailsReceivedButton}
                        >
                          Ver Lista
                        </Button>
                      );
                    })()}
                  </Grid>
                  {/** Trending */}
                  <Grid item xs={2}>
                    <LineChartPreviewCanvas
                      data={buildBgpSeries(
                        sessions,
                        host?.hostId,
                        interfaceName,
                        getBgpItemByInterface,
                      )}
                    />
                  </Grid>
                </Grid>
              </AccordionSummary>
              {/** Accordion Details */}
              {accordionOpen === interfaceName && (
                <AccordionDetails
                  sx={{
                    backgroundColor:
                      index % 2 === 0
                        ? "transparent" // linhas pares
                        : theme.palette.mode == "dark"
                          ? theme.palette.secondary.dark // linhas impares
                          : theme.palette.secondary.lighter, // linhas impares
                  }}
                >
                  {!sessions?.announcedPrefixes?.[host?.hostId] && !!loading ? (
                    <Typography variant="body2">Carregando...</Typography>
                  ) : !sessions?.announcedPrefixes?.[host?.hostId] ? (
                    <Typography variant="body2">
                      Nenhum dado encontrado
                    </Typography>
                  ) : (
                    <Paper sx={styles.paperAccordionDetails} elevation={0}>
                      <Grid container spacing={2}>
                        {/** Esquerda - Anunciados e Recebidos */}
                        <Grid item xs={12} md={6}>
                          <Box sx={styles.detailsColumnContainer}>
                            <Grid item xs={12}>
                              <Box sx={styles.detailsTitleContainer}>
                                <ArrowOutwardOutlined
                                  fontSize="16px"
                                  sx={styles.detailsAnnouncedIcon}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color:
                                      theme.palette.mode === "dark"
                                        ? theme.palette.white.main
                                        : theme.palette.colors.black.darker,
                                  }}
                                >
                                  Prefixos Anunciados
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid container spacing={1}>
                              {CARD_ANNOUNCED?.map((card, i) => (
                                <Grid item xs={6} sm={3} key={i}>
                                  <CardCountPrefixes
                                    title={card?.title}
                                    icon={card?.icon}
                                    titleColor={
                                      theme.palette.mode === "dark"
                                        ? theme.palette.white.main
                                        : theme.palette.colors.black.darker
                                    }
                                    valueColor={
                                      theme.palette.cardGraphic.graphColor
                                        .orangeMedium
                                    }
                                    iconColor={
                                      theme.palette.cardGraphic.graphColor
                                        .orangeMedium
                                    }
                                    value={
                                      card?.format
                                        ? card?.format(
                                            getBgpItemByInterface(
                                              sessions?.[card?.valueIdenfier]?.[
                                                host?.hostId
                                              ],
                                              interfaceName,
                                            ),
                                          )
                                        : (parseInt(
                                            getBgpItemByInterface(
                                              sessions?.[card?.valueIdenfier]?.[
                                                host?.hostId
                                              ],
                                              interfaceName,
                                            )?.value || 0,
                                          ) ?? 0)
                                    }
                                  />
                                </Grid>
                              ))}
                            </Grid>
                            {/** "Prefixos Recebidos"" */}
                            <Grid item xs={12}>
                              <Box sx={styles.detailsReceivedHeader}>
                                <Box sx={styles.detailsReceivedTitleContainer}>
                                  <ArrowOutwardOutlined
                                    fontSize="12px"
                                    sx={styles.detailsReceivedIcon}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={styles.detailsReceivedTitle}
                                  >
                                    Prefixos Recebidos
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid container spacing={1}>
                              {CARD_RECEIVED?.map((card, i) => (
                                <Grid item xs={6} sm={3} key={i}>
                                  <CardCountPrefixes
                                    title={card?.title}
                                    titleColor={
                                      theme.palette.mode === "dark"
                                        ? theme.palette.white.main
                                        : theme.palette.colors.black.darker
                                    }
                                    icon={card?.icon}
                                    valueColor={
                                      theme.palette.colors.blue.medium
                                    }
                                    iconColor={theme.palette.colors.blue.medium}
                                    value={
                                      card?.format
                                        ? card?.format(
                                            getBgpItemByInterface(
                                              sessions?.[card?.valueIdenfier]?.[
                                                host?.hostId
                                              ],
                                              interfaceName,
                                            ),
                                          )
                                        : getBgpItemByInterface(
                                            sessions?.[card?.valueIdenfier]?.[
                                              host?.hostId
                                            ],
                                            interfaceName,
                                          )?.value || 0
                                    }
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Grid>
                        {/** Direita - Gráfico Evolução de Prefixos */}
                        <Grid item xs={12} md={6}>
                          <Box sx={styles.detailsGraphContainer}>
                            {/** Título */}
                            <Box sx={styles.detailsTitleContainer}>
                              <TimelineOutlined
                                sx={styles.detailsEvolutionIcon}
                              />
                              <Typography
                                variant="body2"
                                mb={-1}
                                sx={styles.detailsEvolutionTitle}
                              >
                                Histórico de Prefixos
                              </Typography>
                            </Box>
                            {/** Gráfico */}
                            <LineChartComponent
                              xOptions={{
                                tickFormatter: (clock) =>
                                  dayjs
                                    .unix(clock)
                                    .tz(timeZone)
                                    .format("DD/MM HH:mm"),
                              }}
                              yOptions={{
                                tickFormatter: (value) => value?.toFixed(0),
                              }}
                              maxTicks={10}
                              stackedGraphKeys={["Anunciados", "Recebidos"]}
                              formatValue={(value) =>
                                parseFloat(value)?.toFixed(0)
                              }
                              maxValuebps={Math.ceil(
                                Math.max(
                                  0,
                                  ...((
                                    getBgpItemByInterface(
                                      sessions?.announcedPrefixes?.[
                                        host?.hostId
                                      ],
                                      interfaceName,
                                    ) ?? []
                                  )?.map((item) => item?.value ?? 0) ?? []),
                                  ...((
                                    getBgpItemByInterface(
                                      sessions?.receivedPrefixes?.[
                                        host?.hostId
                                      ],
                                      interfaceName,
                                    ) ?? []
                                  )?.map((item) => item?.value ?? 0) ?? []),
                                ),
                              )}
                              minValuebps={0}
                              objectColorsLine={{
                                Anunciados:
                                  theme?.palette?.colors?.orange?.medium,
                                Recebidos: theme?.palette?.colors?.blue?.medium,
                              }}
                              data={(() => {
                                const announced =
                                  getBgpItemByInterface(
                                    sessions?.announcedPrefixes?.[host?.hostId],
                                    interfaceName,
                                  ) || [];
                                const received =
                                  getBgpItemByInterface(
                                    sessions?.receivedPrefixes?.[host?.hostId],
                                    interfaceName,
                                  ) || [];
                                const mixed = (
                                  Array.isArray(announced) ? announced : []
                                ).map((a) => {
                                  const r = (
                                    Array.isArray(received) ? received : []
                                  ).find((r) => r?.clock === a?.clock);
                                  return {
                                    clock: a?.clock,
                                    Anunciados: a?.value ?? 0,
                                    Recebidos: r?.value ?? 0,
                                  };
                                });
                                return mixed;
                              })()}
                              lineChartOptions={{
                                margin: {
                                  top: 20,
                                  right: 30,
                                  left: 10,
                                  bottom: 0,
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </AccordionDetails>
              )}
            </Accordion>
          ))}
      </Box>
      {/** Pagination */}
      <Paper sx={styles.paperPagination} elevation={0}>
        {filteredBgpItems?.length > ITEMS_PER_PAGE && (
          <TablePagination
            component="div"
            count={bgpItems?.length || 0}
            page={page}
            onPageChange={(_, newPage) => {
              setPage(newPage);
              setAccordionOpen(null);
            }}
            rowsPerPage={ITEMS_PER_PAGE}
            rowsPerPageOptions={[]}
            labelRowsPerPage={null}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        )}
      </Paper>

      {/** Modal de Prefixos Recebidos */}
      <PrefixesModal
        open={modalState?.open}
        onClose={() => setModalState((prev) => ({ ...prev, open: false }))}
        interfaceName={modalState?.interfaceName}
        ipSession={modalState?.ipSession}
        hostName={modalState?.hostName}
        prefixes={modalState?.prefixes}
        loading={modalState?.loading}
      />
    </Box>
  );
}

/** Utils locais */
function LineChartPreviewCanvas({ data }) {
  const canvasRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.length) return;

    const ctx = canvas.getContext("2d");
    canvas.width = canvas.parentElement.clientWidth;
    canvas.height = 10;

    const valuesA = data?.map((d) => d?.Anunciados ?? 0) ?? [];
    const valuesR = data?.map((d) => d?.Recebidos ?? 0) ?? [];
    const max = Math.max(...valuesA, ...valuesR, 1);

    function draw(values, color) {
      if (!Array.isArray(values) || !values.length) return;
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;

      values.forEach((v, i) => {
        const x = (i / (values.length - 1)) * canvas.width;
        const y = canvas.height - (v / max) * canvas.height;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });

      ctx.stroke();
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    draw(valuesA, theme.palette.cardGraphic.graphColor.orangeMedium);
    draw(valuesR, theme.palette.cardGraphic.graphColor.blueMedium);
  }, [data, theme]);

  return <canvas ref={canvasRef} style={{ width: "100%", height: 10 }} />;
}

function buildBgpSeries(sessions, hostId, interfaceName, getBgpItem) {
  const announced =
    getBgpItem(sessions?.announcedPrefixes?.[hostId], interfaceName) || [];
  const received =
    getBgpItem(sessions?.receivedPrefixes?.[hostId], interfaceName) || [];

  return (Array.isArray(announced) ? announced : []).map((a) => ({
    clock: a?.clock,
    Anunciados: a?.value ?? 0,
    Recebidos:
      (Array.isArray(received) ? received : []).find(
        (r) => r?.clock === a?.clock,
      )?.value ?? 0,
  }));
}

function formatDuration(value) {
  const v = Number(value);
  if (!Number.isFinite(v) || v < 0) {
    return { d: null, h: null, m: null, s: null };
  }

  let seconds = Math.floor(v);

  const d = Math.floor(seconds / 86400);
  seconds %= 86400;

  const h = Math.floor(seconds / 3600);
  seconds %= 3600;

  const m = Math.floor(seconds / 60);
  const s = seconds % 60;

  return { d, h, m, s };
}

const CARD_ANNOUNCED = [
  {
    title: "Máximo",
    icon: 1,
    valueIdenfier: "announcedPrefixes",
    format: (data) => {
      const arr = data?.map((item) => item?.value ?? 0) ?? [];
      return arr.length > 0 ? Math.round(Math.max(...arr)) : 0;
    },
  },
  {
    title: "Médio",
    icon: 2,
    valueIdenfier: "announcedPrefixes",
    format: (data) => {
      if (!Array.isArray(data) || data.length === 0) return 0;
      const sum = data?.reduce((acc, item) => acc + (item?.value ?? 0), 0);
      return Math.round(sum / data.length);
    },
  },
  {
    title: "Mínimo",
    icon: 3,
    valueIdenfier: "announcedPrefixes",
    format: (data) => {
      const arr = data?.map((item) => item?.value ?? 0) ?? [];
      return arr.length > 0 ? Math.round(Math.min(...arr)) : 0;
    },
  },
  {
    title: "Atual",
    icon: 4,
    valueIdenfier: "announcedPrefixesLastItem",
  },
];

const CARD_RECEIVED = [
  {
    title: "Máximo",
    icon: 1,
    valueIdenfier: "receivedPrefixes",
    format: (data) => {
      const arr = data?.map((item) => item?.value ?? 0) ?? [];
      return arr.length > 0 ? Math.round(Math.max(...arr)) : 0;
    },
  },
  {
    title: "Médio",
    icon: 2,
    valueIdenfier: "receivedPrefixes",
    format: (data) => {
      const arr = data?.map((item) => item?.value ?? 0) ?? [];
      return arr.length > 0 ? Math.round(Math.min(...arr)) : 0;
    },
  },
  {
    title: "Mínimo",
    icon: 3,
    valueIdenfier: "receivedPrefixes",
    format: (data) => {
      if (!Array.isArray(data) || data.length === 0) return 0;
      const sum = data?.reduce((acc, item) => acc + (item?.value ?? 0), 0);
      return Math.round(sum / data.length);
    },
  },
  {
    title: "Atual",
    icon: 4,
    valueIdenfier: "receivedPrefixesLastItem",
  },
];
